import {
  Button,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Select,
  TimePicker,
  message,
} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {MdOutlineInventory} from 'react-icons/md';

import {
  RQAppointmentAddAPI,
  RqUserLookupAllRole,
} from '../../../../ReactQuery/apis';

const BOOKING_TYPE_OPTIONS = [
  {
    value: 'ONLINE',
    label: 'Online',
  },
  {
    value: 'REFERRED',
    label: 'Referred',
  },
  {
    value: 'WALKIN',
    label: 'Walk-in',
  },
];

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const getEntityId = entity => entity?.id || entity?._id;

const getEntityName = entity =>
  entity?.fullName ||
  entity?.name ||
  entity?.email ||
  'Unknown';

const filterSelectOption = (input, option) =>
  String(option?.label || '')
    .toLowerCase()
    .includes(input.toLowerCase());

/* -------------------------------------------------------------------------- */
/* Add Appointment Modal                                                      */
/* -------------------------------------------------------------------------- */

const AddAppoinment = ({
  open,
  onClose,
  token,
  onCreated,
}) => {
  const [form] = Form.useForm();

  /* ------------------------------------------------------------------------ */
  /* Patient Query                                                            */
  /* ------------------------------------------------------------------------ */

  const {
    data: patientsData = [],
    isFetching: patientsLoading,
  } = RqUserLookupAllRole(
    'PATIENT',
    token,
    {
      enabled: Boolean(open && token),
    },
  );

  /* ------------------------------------------------------------------------ */
  /* Doctor Query                                                             */
  /* ------------------------------------------------------------------------ */

  const {
    data: doctorsData = [],
    isFetching: doctorsLoading,
  } = RqUserLookupAllRole(
    'DOCTOR',
    token,
    {
      enabled: Boolean(open && token),
    },
  );

  /* ------------------------------------------------------------------------ */
  /* Therapist Query                                                          */
  /* ------------------------------------------------------------------------ */

  const {
    data: therapistsData = [],
    isFetching: therapistsLoading,
  } = RqUserLookupAllRole(
    'THERAPIST',
    token,
    {
      enabled: Boolean(open && token),
    },
  );

  /* ------------------------------------------------------------------------ */
  /* Normalize API Data                                                       */
  /* ------------------------------------------------------------------------ */

  const patients = Array.isArray(patientsData)
    ? patientsData
    : [];

  const doctors = Array.isArray(doctorsData)
    ? doctorsData
    : [];

  const therapists = Array.isArray(therapistsData)
    ? therapistsData
    : [];

  /* ------------------------------------------------------------------------ */
  /* Doctor / Therapist Options                                               */
  /* ------------------------------------------------------------------------ */

  const doctorOptions = [
    ...doctors.map(doctor => ({
      ...doctor,
      role: 'Doctor',
    })),

    ...therapists.map(therapist => ({
      ...therapist,
      role: 'Therapist',
    })),
  ];

  /* ------------------------------------------------------------------------ */
  /* Add Appointment Mutation                                                 */
  /* ------------------------------------------------------------------------ */

  const {
    mutate: addAppointment,
    isLoading: submitting,
  } = RQAppointmentAddAPI({
    onSuccess: () => {
      message.success(
        'Appointment booked successfully',
      );

      form.resetFields();

      if (onCreated) {
        onCreated();
      }

      onClose();
    },

    onError: error => {
      message.error(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to book appointment',
      );
    },
  });

  /* ------------------------------------------------------------------------ */
  /* Close Modal                                                              */
  /* ------------------------------------------------------------------------ */

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  /* ------------------------------------------------------------------------ */
  /* Submit                                                                   */
  /* ------------------------------------------------------------------------ */

  const handleFinish = values => {
    if (
      !values.appointmentDate ||
      !values.appointmentTime
    ) {
      return;
    }

    const appointmentDate =
      values.appointmentDate.format(
        'YYYY-MM-DD',
      );

    const appointmentTime =
      values.appointmentTime.format(
        'HH:mm',
      );

    const selectedDateTime = dayjs(
      `${appointmentDate} ${appointmentTime}`,
      'YYYY-MM-DD HH:mm',
    );

    /* Prevent booking a time in the past */
    if (selectedDateTime.isBefore(dayjs())) {
      message.error(
        'Please select a future appointment time.',
      );

      return;
    }

    const formData = {
      patientId: values.patientId,
      doctorId: values.doctorId,
      appointmentDate,
      appointmentTime,
      bookingType: values.bookingType,
      amount:
        values.amount !== undefined &&
        values.amount !== null
          ? Number(values.amount)
          : 0,
    };

    addAppointment({
      formData,
      token,
    });
  };

  /* ------------------------------------------------------------------------ */
  /* Date Validation                                                          */
  /* ------------------------------------------------------------------------ */

  const disabledDate = current => {
    if (!current) {
      return false;
    }

    return current.isBefore(
      dayjs().startOf('day'),
    );
  };

  /* ------------------------------------------------------------------------ */
  /* Time Validation                                                          */
  /* ------------------------------------------------------------------------ */

  const disabledTime = selectedDate => {
    if (!selectedDate) {
      return {};
    }

    const isToday =
      selectedDate.isSame(
        dayjs(),
        'day',
      );

    if (!isToday) {
      return {};
    }

    const currentHour = dayjs().hour();
    const currentMinute = dayjs().minute();

    return {
      disabledHours: () =>
        Array.from(
          {length: currentHour},
          (_, index) => index,
        ),

      disabledMinutes: selectedHour => {
        if (
          selectedHour !== currentHour
        ) {
          return [];
        }

        return Array.from(
          {length: currentMinute},
          (_, index) => index,
        );
      },
    };
  };

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      width={520}
      title={(
        <div
          className={
            'flex items-center gap-2'
          }
        >
          <div
            className={
              'w-9 h-9 rounded-lg flex items-center justify-center text-white'
            }
            style={{
              background:
                'linear-gradient(135deg, #b69c66, #b69c66)',
            }}
          >
            <MdOutlineInventory />
          </div>

          <span
            className={
              'text-primary-700 font-bold text-base'
            }
          >
            Book Appointment
          </span>
        </div>
      )}
    >
      <Form
        form={form}
        layout={'vertical'}
        onFinish={handleFinish}
        className={'mt-4'}
        preserve={false}
      >
        {/* ---------------------------------------------------------------- */}
        {/* Patient                                                          */}
        {/* ---------------------------------------------------------------- */}

        <Form.Item
          label={(
            <span
              className={
                'font-medium text-primary-700'
              }
            >
              Patient
            </span>
          )}
          name={'patientId'}
          rules={[
            {
              required: true,
              message:
                'Please select a patient',
            },
          ]}
        >
          <Select
            showSearch
            allowClear
            placeholder={'Select patient'}
            loading={patientsLoading}
            optionFilterProp={'label'}
            filterOption={
              filterSelectOption
            }
            options={patients
              .map(patient => {
                const id =
                  getEntityId(patient);

                if (!id) {
                  return null;
                }

                return {
                  value: id,
                  label:
                    getEntityName(
                      patient,
                    ),
                };
              })
              .filter(Boolean)}
          />
        </Form.Item>

        {/* ---------------------------------------------------------------- */}
        {/* Doctor / Therapist                                               */}
        {/* ---------------------------------------------------------------- */}

        <Form.Item
          label={(
            <span
              className={
                'font-medium text-primary-700'
              }
            >
              Doctor
            </span>
          )}
          name={'doctorId'}
          rules={[
            {
              required: true,
              message:
                'Please select a doctor',
            },
          ]}
        >
          <Select
            showSearch
            allowClear
            placeholder={'Select doctor'}
            loading={
              doctorsLoading ||
              therapistsLoading
            }
            optionFilterProp={'label'}
            filterOption={
              filterSelectOption
            }
            options={doctorOptions
              .map(doctor => {
                const id =
                  getEntityId(doctor);

                if (!id) {
                  return null;
                }

                return {
                  value: id,
                  label: `${getEntityName(
                    doctor,
                  )} — ${doctor.role}`,
                };
              })
              .filter(Boolean)}
          />
        </Form.Item>

        {/* ---------------------------------------------------------------- */}
        {/* Date / Time                                                      */}
        {/* ---------------------------------------------------------------- */}

        <div
          className={
            'grid grid-cols-1 sm:grid-cols-2 gap-4'
          }
        >
          <Form.Item
            label={(
              <span
                className={
                  'font-medium text-primary-700'
                }
              >
                Date
              </span>
            )}
            name={'appointmentDate'}
            rules={[
              {
                required: true,
                message:
                  'Please pick a date',
              },
            ]}
          >
            <DatePicker
              className={'w-full'}
              format={'YYYY-MM-DD'}
              disabledDate={
                disabledDate
              }
            />
          </Form.Item>

          <Form.Item
            label={(
              <span
                className={
                  'font-medium text-primary-700'
                }
              >
                Time
              </span>
            )}
            name={'appointmentTime'}
            rules={[
              {
                required: true,
                message:
                  'Please pick a time',
              },
            ]}
          >
            <TimePicker
              className={'w-full'}
              format={'HH:mm'}
              minuteStep={5}
              disabledTime={() =>
                disabledTime(
                  form.getFieldValue(
                    'appointmentDate',
                  ),
                )
              }
            />
          </Form.Item>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Booking Type / Amount                                            */}
        {/* ---------------------------------------------------------------- */}

        <div
          className={
            'grid grid-cols-1 sm:grid-cols-2 gap-4'
          }
        >
          <Form.Item
            label={(
              <span
                className={
                  'font-medium text-primary-700'
                }
              >
                Booking Type
              </span>
            )}
            name={'bookingType'}
            initialValue={'ONLINE'}
            rules={[
              {
                required: true,
                message:
                  'Please select a booking type',
              },
            ]}
          >
            <Select
              options={
                BOOKING_TYPE_OPTIONS
              }
            />
          </Form.Item>

          <Form.Item
            label={(
              <span
                className={
                  'font-medium text-primary-700'
                }
              >
                Amount
              </span>
            )}
            name={'amount'}
            rules={[
              {
                required: true,
                message:
                  'Please enter the amount',
              },
              {
                type: 'number',
                min: 0,
                message:
                  'Amount must be 0 or greater',
              },
            ]}
          >
            <InputNumber
              className={'w-full'}
              min={0}
              step={0.01}
              precision={2}
              prefix={'₹'}
              placeholder={'0.00'}
            />
          </Form.Item>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Actions                                                          */}
        {/* ---------------------------------------------------------------- */}

        <div
          className={
            'flex justify-end gap-2 mt-6'
          }
        >
          <Button
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>

          <Button
            type={'primary'}
            htmlType={'submit'}
            loading={submitting}
            className={
              'bg-primary-700 hover:!bg-primary-800 border-none'
            }
          >
            Book Appointment
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

AddAppoinment.propTypes = {
  open: PropTypes.bool.isRequired,

  onClose: PropTypes.func.isRequired,

  token: PropTypes.string,

  onCreated: PropTypes.func,
};

export default AddAppoinment;
